import { coverageLinesFor } from "@kovernow/flows/quote";
import type { CoverageLine, UnderwriterCoverage } from "@kovernow/types/tenant-web-config";
import { Check, ExternalLink, Minus } from "lucide-react";
import { useT } from "../../core/use-t";

/**
 * "What's covered / not covered" from the underwriter's tenant config, in
 * full, plus the policy wording link. No config → nothing (never blocks).
 */
export function CoverageSummary({ coverage, classification }: { coverage?: UnderwriterCoverage; classification?: string }) {
  const t = useT();
  if (!coverage) return null;
  const covered = coverageLinesFor(coverage.covered, classification);
  const notCovered = coverageLinesFor(coverage.notCovered, classification);
  return (
    <section aria-label={t("choosePlan.coverage.label")} className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4" data-testid="coverage-summary">
      {covered.length ? <Lines headingKey="choosePlan.coverage.covered" lines={covered} included /> : null}
      {notCovered.length ? <Lines headingKey="choosePlan.coverage.notCovered" lines={notCovered} included={false} /> : null}
      <a
        href={coverage.wordingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-h-11 items-center gap-1 self-start font-semibold text-primary underline underline-offset-4"
      >
        {t("choosePlan.coverage.wording")}
        <ExternalLink aria-hidden="true" className="size-4" />
        <span className="sr-only"> {t("choosePlan.coverage.newTab")}</span>
      </a>
    </section>
  );
}

function Lines({ headingKey, lines, included }: { headingKey: string; lines: CoverageLine[]; included: boolean }) {
  const t = useT();
  const Icon = included ? Check : Minus;
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-semibold">{t(headingKey)}</h3>
      <ul className="flex flex-col gap-2">
        {lines.map((l, i) => (
          <li key={`${l.title}-${i}`} className="flex items-start gap-2">
            <Icon aria-hidden="true" className={included ? "mt-0.5 size-4 shrink-0 text-status-success" : "mt-0.5 size-4 shrink-0 text-muted-foreground"} />
            <span className="flex flex-col">
              <span className="font-medium">{l.title}</span>
              {l.detail ? <span className="text-sm text-muted-foreground">{l.detail}</span> : null}
              {l.note ? <span className="text-xs text-muted-foreground">{l.note}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
